import { v4 as uuidv4 } from 'uuid';
import { crdtServiceWS } from './crdtServiceWS';
import { createFileLogger } from '../../utils/logger';

export interface SessionData {
  x: number;
  y: number;
  user: {
    id: string;
    name: string;
    email?: string;
    picture?: string;
  };
  color: string;
  lastSeen: number;
}

class PresenceService {
  private sessionId: string;
  private currentSpaceId: string | null = null;
  private disconnectFn: (() => void) | null = null;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private lastUpdateTime: number = 0;
  private throttleDelay: number = 100; // Throttle to max 10 updates per second

  constructor() {
    // Generate unique session ID for this tab
    this.sessionId = uuidv4();
    
    // Setup multiple cleanup events for reliability
    if (typeof window !== 'undefined') {
      const cleanupHandler = () => {
        this.immediateCleanup();
      };
      
      // Multiple events to catch different ways users can leave
      window.addEventListener('beforeunload', cleanupHandler);
      window.addEventListener('pagehide', cleanupHandler);
      window.addEventListener('unload', cleanupHandler);
      
      // ✅ REMOVED: Don't cleanup presence on tab visibility change - only on actual page unload
      // Tab switches should not disconnect presence
      // document.addEventListener('visibilitychange', () => {
      //   if (document.visibilityState === 'hidden') {
      //     this.immediateCleanup();
      //   }
      // });
    }
  }

  /**
   * Connect to a space and start receiving presence updates
   */
  connect(spaceId: string, onUpdate: (sessions: Record<string, SessionData>) => void): () => void {
    if (this.currentSpaceId) {
      this.disconnect();
    }

    this.currentSpaceId = spaceId;
    const key = `presence-${spaceId}`;

    // Connect to CRDT for real-time updates
    this.disconnectFn = crdtServiceWS.connect<Record<string, SessionData>>(key, (update) => {
      try {
        if (update?.data) {
          // Filter out stale sessions (older than 30 seconds)
          const now = Date.now();
          const activeSessions: Record<string, SessionData> = {};
          
          Object.entries(update.data).forEach(([sessionId, session]) => {
            if (session && now - session.lastSeen < 10000) { // 10 second timeout (faster cleanup)
              activeSessions[sessionId] = session;
            }
          });
          
          // Remove our own session from the display
          delete activeSessions[this.sessionId];
          
          onUpdate(activeSessions);
        }
      } catch (error) {
        const log = createFileLogger('PRESENCE_SERVICE');
        log.error('Error processing presence update:', error);
      }
    });

    // Start periodic cleanup every 3 seconds for faster removal
    this.cleanupInterval = setInterval(() => {
      try {
        this.cleanupStalePresence(spaceId);
      } catch (error) {
        const log = createFileLogger('PRESENCE_SERVICE');
        log.error('Error in periodic cleanup:', error);
      }
    }, 3000);

    // Return disconnect function
    return () => this.disconnect();
  }

  /**
   * Update cursor position for this session
   */
  updateCursor(x: number, y: number, user: any): void {
    // CRITICAL FIX: Throttle updates to prevent overwhelming Y.js garbage collection
    const now = Date.now();
    if (now - this.lastUpdateTime < this.throttleDelay) {
      return;
    }
    this.lastUpdateTime = now;
    
    if (!this.currentSpaceId) {
      return;
    }

    const key = `presence-${this.currentSpaceId}`;
    
    // Ensure all data is serializable and doesn't contain circular references
    const sessionData: SessionData = {
      x: Number(x) || 0,
      y: Number(y) || 0,
      user: {
        id: String(user?.sub || 'anonymous'),
        name: String(user?.name || 'Anonymous User'),
        email: user?.email ? String(user.email) : undefined,
        picture: user?.picture ? String(user.picture) : undefined,
      },
      color: this.getUserColor(user?.sub || 'anonymous'),
      lastSeen: Date.now(),
    };

    try {
      // Update immediately with no debouncing
      crdtServiceWS.update(key, {
        [this.sessionId]: sessionData
      }, {
        preserveOtherConnections: true,
        updateType: 'update'
      });
    } catch (error) {
      const log = createFileLogger('PRESENCE_SERVICE');
      log.error(`Error in updateCursor for key ${key}:`, error);
      // Don't rethrow to prevent breaking the app
    }
  }

  /**
   * Remove this session's presence
   */
  removePresence(): void {
    if (!this.currentSpaceId) return;

    const key = `presence-${this.currentSpaceId}`;
    
    try {
      // Get current data to find all flattened keys for this session
      const doc = crdtServiceWS.get(key);
      if (doc) {
        const ymap = doc.getMap(key);
        const flatData = ymap.toJSON();
        const toRemove: Record<string, null> = {};
        
        // Remove all flattened keys for this session
        Object.keys(flatData).forEach(flatKey => {
          if (flatKey.startsWith(this.sessionId + '.')) {
            toRemove[flatKey] = null;
          }
        });
        toRemove[this.sessionId] = null;
        
        if (Object.keys(toRemove).length > 0) {
          const log = createFileLogger('PRESENCE_SERVICE');
          log.info(`Removing presence ${Object.keys(toRemove).length} keys for session ${this.sessionId}`);
          crdtServiceWS.update(key, toRemove, {
            preserveOtherConnections: true,
            updateType: 'remove'
          });
        }
      } else {
        // Fallback to simple removal if doc doesn't exist
        crdtServiceWS.update(key, {
          [this.sessionId]: null
        }, {
          preserveOtherConnections: true,
          updateType: 'remove'
        });
      }
    } catch (error) {
      const log = createFileLogger('PRESENCE_SERVICE');
      log.warn('Error removing presence:', error);
    }
  }

  /**
   * Disconnect from current space
   */
  disconnect(): void {
    if (this.disconnectFn) {
      this.disconnectFn();
      this.disconnectFn = null;
    }

    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    this.removePresence();
    this.currentSpaceId = null;
  }

  /**
   * Cleanup stale presence data
   */
  private cleanupStalePresence(spaceId: string): void {
    const key = `presence-${spaceId}`;
    const doc = crdtServiceWS.get(key);
    if (!doc) return;

    const ymap = doc.getMap(key);
    const flatData = ymap.toJSON();
    if (!flatData) return;

    const now = Date.now();
    const toRemove: Record<string, null> = {};
    const staleSessionIds = new Set<string>();

    // Find stale sessions by checking flattened lastSeen values
    Object.entries(flatData).forEach(([key, value]) => {
      if (key.endsWith('.lastSeen') && typeof value === 'number') {
        const sessionId = key.replace('.lastSeen', '');
        if (now - value > 10000) { // 10 seconds
          staleSessionIds.add(sessionId);
          const log = createFileLogger('PRESENCE_SERVICE');
          log.info(`Found stale session ${sessionId}, lastSeen ${value}, age: ${now - value}ms`);
        }
      }
    });

    // Mark all flattened keys for stale sessions for removal
    staleSessionIds.forEach(sessionId => {
      // Find all flattened keys for this session and mark them for removal
      Object.keys(flatData).forEach(flatKey => {
        if (flatKey.startsWith(sessionId + '.')) {
          toRemove[flatKey] = null;
        }
      });
      // Also mark the base session key if it exists
      toRemove[sessionId] = null;
    });

    // Remove stale sessions
    if (Object.keys(toRemove).length > 0) {
      const log = createFileLogger('PRESENCE_SERVICE');
      log.info(`Removing ${staleSessionIds.size} stale sessions (${Object.keys(toRemove).length} keys):`, Array.from(staleSessionIds));
      crdtServiceWS.update(key, toRemove, {
        preserveOtherConnections: true,
        updateType: 'remove'
      });
    }
  }

  /**
   * Get a consistent color for a user
   */
  private getUserColor(userId: string): string {
    const colors = [
      '#FF5733', '#33FF57', '#3357FF', '#FF33A8', 
      '#33FFF6', '#F633FF', '#FFF633', '#FF8C33',
      '#8C33FF', '#33FF8C'
    ];
    
    // Use user ID to consistently assign same color
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      hash = ((hash << 5) - hash + userId.charCodeAt(i)) & 0xffffffff;
    }
    
    return colors[Math.abs(hash) % colors.length];
  }

  /**
   * Immediate cleanup for page unload events (synchronous)
   */
  immediateCleanup(): void {
    if (!this.currentSpaceId) return;

    // Immediately remove presence with minimal processing
    const key = `presence-${this.currentSpaceId}`;
    try {
      // Get current data to find all flattened keys for this session
      const doc = crdtServiceWS.get(key);
      if (doc) {
        const ymap = doc.getMap(key);
        const flatData = ymap.toJSON();
        const toRemove: Record<string, null> = {};
        
        // Remove all flattened keys for this session
        Object.keys(flatData).forEach(flatKey => {
          if (flatKey.startsWith(this.sessionId + '.')) {
            toRemove[flatKey] = null;
          }
        });
        toRemove[this.sessionId] = null;
        
        if (Object.keys(toRemove).length > 0) {
          const log = createFileLogger('PRESENCE_SERVICE');
          log.info(`Immediate cleanup removing ${Object.keys(toRemove).length} keys for session ${this.sessionId}`);
          crdtServiceWS.update(key, toRemove, {
            preserveOtherConnections: true,
            updateType: 'remove'
          });
        }
      } else {
        // Fallback to simple removal if doc doesn't exist
        crdtServiceWS.update(key, {
          [this.sessionId]: null
        }, {
          preserveOtherConnections: true,
          updateType: 'remove'
        });
      }
    } catch (error) {
      // Ignore errors during immediate cleanup
      const log = createFileLogger('PRESENCE_SERVICE');
      log.warn('Error during immediate cleanup:', error);
    }
  }

  /**
   * Force cleanup all presence data (for testing)
   */
  cleanup(): void {
    this.disconnect();
  }

  /**
   * Get session ID for this tab
   */
  getSessionId(): string {
    return this.sessionId;
  }

  /**
   * Test function to simulate leaving (for testing)
   */
  testLeaving(): void {
    const log = createFileLogger('PRESENCE_SERVICE');
    log.info(`Testing immediate cleanup for session ${this.sessionId}`);
    this.immediateCleanup();
  }

  /**
   * Test function to check current presence data
   */
  testGetPresence(): any {
    if (!this.currentSpaceId) return null;
    
    const key = `presence-${this.currentSpaceId}`;
    const doc = crdtServiceWS.get(key);
    if (!doc) return null;

    const ymap = doc.getMap(key);
    const flatData = ymap.toJSON();
    
    // Also show timestamps and ages
    const now = Date.now();
    const analysis: any = { raw: flatData, sessions: {} };
    
    // Parse flattened data to show session info
    const sessionIds = new Set<string>();
    Object.keys(flatData).forEach(key => {
      if (key.includes('.')) {
        const sessionId = key.split('.')[0];
        sessionIds.add(sessionId);
      }
    });
    
    sessionIds.forEach(sessionId => {
      const lastSeen = flatData[`${sessionId}.lastSeen`];
      if (lastSeen) {
        analysis.sessions[sessionId] = {
          lastSeen,
          ageMs: now - lastSeen,
          isStale: (now - lastSeen) > 10000,
          user: flatData[`${sessionId}.user.name`] || 'Unknown'
        };
      }
    });
    
    return analysis;
  }

  /**
   * Force cleanup immediately (for testing)
   */
  testForceCleanup(): void {
    if (!this.currentSpaceId) return;
    const log = createFileLogger('PRESENCE_SERVICE');
    log.info('Forcing immediate cleanup...');
    this.cleanupStalePresence(this.currentSpaceId);
  }
}

// Export singleton instance
export const presenceService = new PresenceService();

// Add global test functions for debugging
if (typeof window !== 'undefined') {
  (window as any).testPresence = {
    // Test leaving immediately
    leave: () => {
      presenceService.testLeaving();
    },
    
    // Check current presence data
    check: () => {
      const data = presenceService.testGetPresence();
      const log = createFileLogger('PRESENCE_SERVICE');
      log.info('Current presence data:', data);
      return data;
    },
    
    // Force cleanup of stale sessions
    cleanup: () => {
      presenceService.testForceCleanup();
    },
    
    // Get session ID
    getSessionId: () => {
      return presenceService.getSessionId();
    }
  };
} 